"use client";
import React, {
  FC,
  HTMLAttributes,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";
import Script from "next/script";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useTranslations } from "next-intl";

export interface GoogleMapsProps {
  lat: number;
  lng: number;
  height: any;
  width: any;
}

type Address = {
  address1: string;
  address2?: string;
  address3?: string;
  city: string;
  country: string;
  state: string;
  zip: string;
};

const GOOGLE_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
const BASE_URL = "https://maps.googleapis.com/maps/api/js";
const PARAMS = new URLSearchParams({
  key: GOOGLE_API_KEY,
  libraries: "places",
});

const isLocalhost = () =>
  typeof window !== "undefined" && window.location.hostname === "localhost";

function createAddressString({ address1, city, state }: Address) {
  return [address1, city, state].join();
}

const MockMap: FC<
  HTMLAttributes<HTMLDivElement> & {
    address: Address;
    height: any;
    width: any;
  }
> = memo(function MockMap({ height, width, ...props }) {
  const t = useTranslations("GoogleMap");
  return (
    <div
      {...props}
      className="relative overflow-hidden aspect-square h-96 w-full bg-gray-200 flex items-center justify-center text-gray-500"
      style={{ height: height, width: width }}
      data-testid={applyDataTestId(pom.merchantFooter.map)}
    >
      {t("mockGoogleMap")}
    </div>
  );
});

export const Map: FC<
  HTMLAttributes<HTMLDivElement> & {
    address: Address;
    height: any;
    width: any;
  }
> = memo(function Map({ address, height, width, ...props }) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    if (window.google && window.google.maps) {
      setIsScriptLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isLocalhost()) return; // Skip effect if on localhost

    if (!isScriptLoaded || !mapRef.current) return;

    const geoCoder = new google.maps.Geocoder();

    geoCoder.geocode(
      { address: createAddressString(address) },
      (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results) {
          console.error(`Geocode failed with status: ${status}`);
          return;
        }

        const { location } = results[0].geometry;

        const map = new google.maps.Map(mapRef.current!, {
          center: location,
          zoom: 15,
        });

        new google.maps.Marker({ position: location, map });
      }
    );
  }, [address, isScriptLoaded]);

  if (isLocalhost()) {
    return (
      <MockMap address={address} height={height} width={width} {...props} />
    );
  }

  return (
    <div {...props}>
      <Script
        src={`${BASE_URL}?${PARAMS.toString()}`}
        onLoad={() => setIsScriptLoaded(true)}
      />
      <div
        className="relative overflow-hidden aspect-square h-96 w-full"
        style={{ height: height, width: width }}
        ref={mapRef}
        data-testid={applyDataTestId(pom.merchantFooter.map)}
      />
    </div>
  );
});
